/**
 * Route manifest for the analytics + lending slice of S2.
 *
 * Six Next.js pages moved in this batch:
 *
 *   /analytics        `src/app/(dashboard)/analytics/page.tsx`
 *   /budget           `src/app/(dashboard)/budget/page.tsx`
 *   /yearly-summary   `src/app/(dashboard)/yearly-summary/page.tsx`
 *   /loans            `src/app/(dashboard)/loans/page.tsx`
 *   /loans/:id        `src/app/(dashboard)/loans/[id]/page.tsx`
 *   /home-loan        `src/app/(dashboard)/home-loan/page.tsx`
 *
 * `routes.tsx` is NOT edited by this port — it generates one `<prefix>/*`
 * route per `PAGE_REGISTRY` entry and picks the element out of its own
 * `IMPLEMENTED` map. Two shapes are therefore exported here so the orchestrator
 * can wire whichever way it prefers, with no edit to this file either way:
 *
 *   - `INSIGHTS_PAGES` — `PageKey -> element`, to spread into `IMPLEMENTED`.
 *     This is the preferred wiring: the registry keeps owning the paths, so a
 *     page still cannot exist without a permission row (SAFE-04).
 *   - `insightsRoutes` — self-contained `RouteObject[]` using the same
 *     `"<matchPrefix>/*"` child-path convention `routes.tsx` builds, for a
 *     spread into the guarded layout's `children`.
 *
 * `/loans` is the only prefix with a sub-route, and both wirings resolve it the
 * same way: `LoansRoutes` matches `""` and `":id"` relative to whatever mounted
 * it, so the registry still produces exactly one `loans/*` path.
 *
 * ADMIN GATING (SAFE-04): nothing about `/home-loan` is decided here. Its
 * registry row carries `adminOnly: true`, which keeps the key out of the
 * permission grant matrix, so a USER has no `home-loan` row and
 * `resolvePermission` denies — `RequireAuth` redirects to
 * `/no-access?page=home-loan` before this element is ever reached. FastAPI's
 * `require_homeloan_access` is the real enforcement, and `LoansPage` still
 * checks `role === "ADMIN" && useCanViewPage("home-loan")` before drawing the
 * Home Loan tab. Three independent checks, exactly as on Next.js.
 */
import { lazy, Suspense, type ReactNode } from "react";
import { useRoutes, type RouteObject } from "react-router-dom";
import type { PageKey } from "@/lib/rbac/pages";
import { RouteSkeleton } from "@/lib/prefetch";

// S3: lazy — these five pages (recharts-heavy, several with their own PDF/CSV
// export paths) previously shipped in the main chunk for every visitor
// regardless of role; `home-loan` in particular is admin-only and most of the
// eight family accounts should never download it at all.
const AnalyticsPage = lazy(() => import("@/pages/analytics/AnalyticsPage"));
const BudgetPage = lazy(() => import("@/pages/budget/BudgetPage"));
const YearlySummaryPage = lazy(() => import("@/pages/yearly-summary/YearlySummaryPage"));
const LoansPage = lazy(() => import("@/pages/loans/LoansPage"));
const LoanDetailPage = lazy(() => import("@/pages/loans/LoanDetailPage"));
const HomeLoanPage = lazy(() => import("@/pages/home-loan/HomeLoanPage"));

function s(element: ReactNode): ReactNode {
  return <Suspense fallback={<RouteSkeleton />}>{element}</Suspense>;
}

/**
 * `/loans` and `/loans/:id`.
 *
 * The list is an INDEX route, not a layout element, so it unmounts when the
 * detail page opens — leaving it mounted underneath would keep its `["loans"]`
 * query refetching behind a route the user has left.
 */
const LOANS_CHILDREN: RouteObject[] = [
  { index: true, element: s(<LoansPage />) },
  { path: ":id", element: s(<LoanDetailPage />) },
];

/** The `/loans` prefix as a single element, for the `IMPLEMENTED`-map wiring. */
export function LoansRoutes() {
  return useRoutes(LOANS_CHILDREN);
}

/** `PageKey -> element`, for `routes.tsx`'s `IMPLEMENTED` map. */
export const INSIGHTS_PAGES: Partial<Record<PageKey, ReactNode>> = {
  analytics: s(<AnalyticsPage />),
  budget: s(<BudgetPage />),
  "yearly-summary": s(<YearlySummaryPage />),
  loans: <LoansRoutes />,
  "home-loan": s(<HomeLoanPage />),
};

/**
 * Self-contained route table, in `PAGE_REGISTRY` order, using the same
 * `"<matchPrefix>/*"` child paths `routes.tsx` generates.
 */
export const insightsRoutes: RouteObject[] = [
  { path: "analytics/*", element: s(<AnalyticsPage />) },
  { path: "loans/*", children: LOANS_CHILDREN },
  { path: "home-loan/*", element: s(<HomeLoanPage />) },
  { path: "budget/*", element: s(<BudgetPage />) },
  { path: "yearly-summary/*", element: s(<YearlySummaryPage />) },
];
